import { App, Spin } from "antd";
import { Form, FormikProvider, useFormik } from "formik";
import { useAtom, useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import Upload from "../../custom/upload/upload";
import { useGetRequiredDocumentsQuery } from "../../hooks/api/useGetRequiredDocumentsQuery";
import { useUploadRequiredDocumentMutation } from "../../hooks/api/useUploadRequiredDocumentMutation";
import { pageIndexAtom, userAtom } from "../../utils/store";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import styles from "./styles.module.scss";

const REQUIRED_DOCUMENTS_STORAGE_KEY = "required-documents-upload";
const REQUIRED_DOCUMENTS_COMPLETED_KEY = "required-documents-completed";

interface UploadedRequiredDocumentMeta {
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  viewUrl?: string;
}

interface RequiredDocumentsFormValues {
  documents: Record<string, File | null>;
}

const getStoredRequiredDocuments = () => {
  try {
    return JSON.parse(
      localStorage.getItem(REQUIRED_DOCUMENTS_STORAGE_KEY) ?? "{}",
    ) as Record<string, UploadedRequiredDocumentMeta>;
  } catch (_error) {
    return {};
  }
};

const getUploadedDocumentViewUrl = (response: any, fallbackUrl?: string) => {
  const responseData = response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return (
    responseData?.fileUrl ||
    responseData?.documentUrl ||
    responseData?.url ||
    response?.fileUrl ||
    response?.documentUrl ||
    response?.url ||
    fallbackUrl
  );
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(file);
  });

const normalizeAllowedFileTypes = (allowedFileTypes?: string) =>
  allowedFileTypes
    ?.split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (item.startsWith(".") ? item.toLowerCase() : `.${item.toLowerCase()}`)) ?? [];

const getDocumentKey = (documentId: number) => `${documentId}`;

const RequiredDocument = () => {
  const { notification } = App.useApp();
  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const { documents, isLoading, isError, error } = useGetRequiredDocumentsQuery({
    programTypeId: user?.programTypeId,
  });
  const { uploadHandler, isLoading: isUploading } =
    useUploadRequiredDocumentMutation();

  const [uploadedDocuments, setUploadedDocuments] = useState<
    Record<string, UploadedRequiredDocumentMeta>
  >(getStoredRequiredDocuments);
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, UploadedRequiredDocumentMeta>
  >({});
  const [uploadingDocumentId, setUploadingDocumentId] = useState<number | null>(
    null,
  );

  const initialValues = useMemo<RequiredDocumentsFormValues>(
    () => ({
      documents:
        documents?.reduce<Record<string, File | null>>((accumulator, item) => {
          accumulator[getDocumentKey(item.id)] = null;
          return accumulator;
        }, {}) ?? {},
    }),
    [documents],
  );

  const formik = useFormik<RequiredDocumentsFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async () => {
      localStorage.setItem(REQUIRED_DOCUMENTS_COMPLETED_KEY, "true");
      setPageIndex(pageIndex + 1);
    },
  });

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleFileChange =
    (item: RequiredDocument) => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const documentKey = getDocumentKey(item.id);

      if (!file) {
        return;
      }

      const allowedFormats = normalizeAllowedFileTypes(item.allowedFileTypes);
      const fileExtension = file.name.includes(".")
        ? `.${file.name.split(".").pop()?.toLowerCase()}`
        : "";
      const maxFileSizeInBytes = item.maxFileSizeInMB * 1024 * 1024;

      if (allowedFormats.length > 0 && !allowedFormats.includes(fileExtension)) {
        notification.error({
          message: "Invalid file type",
          description: `Allowed file types: ${allowedFormats.join(", ")}`,
        });
        event.target.value = "";
        return;
      }

      if (maxFileSizeInBytes > 0 && file.size > maxFileSizeInBytes) {
        notification.error({
          message: "File too large",
          description: `${item.name} must be less than ${item.maxFileSizeInMB}MB`,
        });
        event.target.value = "";
        return;
      }

      const uploadedFileMeta = {
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
      };

      await formik.setFieldValue(`documents.${documentKey}`, file);
      setSelectedFiles((currentValue) => ({
        ...currentValue,
        [documentKey]: uploadedFileMeta,
      }));
      localStorage.removeItem(REQUIRED_DOCUMENTS_COMPLETED_KEY);

      try {
        setUploadingDocumentId(item.id);
        const localPreviewUrl = await readFileAsDataUrl(file);
        const response = await uploadHandler({
          requiredDocumentId: item.id,
          file,
        });

        const updatedUploadedDocuments = {
          ...uploadedDocuments,
          [documentKey]: uploadedFileMeta,
        };
        const resolvedViewUrl = getUploadedDocumentViewUrl(
          response,
          localPreviewUrl,
        );

        updatedUploadedDocuments[documentKey] = {
          ...uploadedFileMeta,
          viewUrl: resolvedViewUrl,
        };

        setUploadedDocuments(updatedUploadedDocuments);
        localStorage.setItem(
          REQUIRED_DOCUMENTS_STORAGE_KEY,
          JSON.stringify(updatedUploadedDocuments),
        );
        await formik.setFieldValue(`documents.${documentKey}`, null);
      } catch (_error) {
        setSelectedFiles((currentValue) => {
          const updatedValue = { ...currentValue };
          delete updatedValue[documentKey];
          return updatedValue;
        });
        await formik.setFieldValue(`documents.${documentKey}`, null);
        event.target.value = "";
      } finally {
        setUploadingDocumentId(null);
      }
    };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>{error?.message}</div>;
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <section className={styles.section}>
          <h2>Required Documents</h2>
          <br />
          <p>Upload the documents requested for your application.</p>
        </section>
        <hr />

        <div className={styles.section}>
          {documents?.length ? (
            <div className={styles.formWrapper}>
              {documents.map((item) => {
                const allowedFormats = normalizeAllowedFileTypes(
                  item.allowedFileTypes,
                );
                const documentKey = getDocumentKey(item.id);
                const currentFile =
                  selectedFiles[documentKey] || uploadedDocuments[documentKey];

                return (
                  <div key={item.id}>
                    <Upload
                      name={`documents.${documentKey}`}
                      label={`${item.name}${item.isRequired ? " *" : ""}`}
                      description={
                        <div>
                          <p>{item.description}</p>
                          <small>
                            Max size: {item.maxFileSizeInMB}MB
                            {allowedFormats.length > 0
                              ? ` | Allowed: ${allowedFormats.join(", ")}`
                              : ""}
                          </small>
                        </div>
                      }
                      fileName={currentFile?.fileName}
                      fileSize={currentFile?.fileSize}
                      allowedFormats={allowedFormats}
                      accept={allowedFormats.join(",")}
                      onChange={handleFileChange(item)}
                    />
                    {uploadingDocumentId === item.id && (
                      <div className={styles.loader}>
                        <Spin size="small" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No required documents found for this application.</p>
          )}

          <OnboardingButtonGroup
            onClick={handlePrevClick}
            onNextClick={formik.submitForm}
            isLoading={isUploading}
            disabled={uploadingDocumentId !== null}
          />
        </div>
      </Form>
    </FormikProvider>
  );
};

export default RequiredDocument;
